import PropTypes from 'prop-types'
import React, { useState, useEffect, useMemo } from 'react'
import { Form, useNotify, useRefresh, useEditController } from 'react-admin'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Adjustment from '../../../VehicleAdjustments/Adjustment'
import {
  calculatePrices,
  calculateCommission,
} from '../../../../Utils/RestUtils'
import { calculateAdjustmentPrice } from '../../../../Utils/PricingUtils'
import { autoSaveField } from '../../../../Utils/AutoSave'
import { sendLog } from '../../../../Class/AuditLog'
import { usePriceModalContext } from '../../../../Utils/providers/PriceModalProvider'

const BestPriceModal = ({ sortedAdjustments }) => {
  const { record, refetch } = useEditController()
  const notify = useNotify()
  const refresh = useRefresh()
  const { activeModal, closeModal, modalPayload } = usePriceModalContext()

  const [isLoadingCalculate, setIsLoadingCalculate] = useState(false)
  const topScrapPrice = modalPayload?.topScrapPrice ?? 0
  const [guidePrice, setGuidePrice] = useState(0)
  const [targetPrice, setTargetPrice] = useState(0)
  const [finalPrice, setFinalPrice] = useState(0)
  const [chosenValue, setChosenValue] = useState(0)

  const [keyAdjustments, setKeyAdjustments] = useState(null)
  const allowCalculateMaxPriceButton = useMemo(() => {
    if (!keyAdjustments) return false

    const allConfirmed =
      keyAdjustments.leather.confirmed &&
      keyAdjustments.satNav.confirmed &&
      keyAdjustments.keys.confirmed &&
      keyAdjustments.history.confirmed

    return allConfirmed
  }, [keyAdjustments])

  const allowFinalPriceInput = useMemo(() => {
    return finalPrice > 0
  }, [finalPrice])

  const resetModalState = () => {
    setGuidePrice(0)
    setTargetPrice(0)
    setFinalPrice(0)
    setChosenValue(0)
    setKeyAdjustments(null)
  }

  const showRulePriceMessage = useMemo(() => {
    return (
      guidePrice > 0 &&
      targetPrice > 0 &&
      finalPrice > 0 &&
      guidePrice === targetPrice &&
      targetPrice === finalPrice
    )
  }, [guidePrice, targetPrice, finalPrice])

  const showSalvageMessage = useMemo(() => {
    const scrapPrice = Number(topScrapPrice)
    const maxPrice = Number(finalPrice)

    return (
      allowCalculateMaxPriceButton &&
      scrapPrice > 0 &&
      maxPrice > 0 &&
      scrapPrice > maxPrice
    )
  }, [topScrapPrice, finalPrice, allowCalculateMaxPriceButton])

  const handleCalculate = async () => {
    if (isLoadingCalculate) return
    setIsLoadingCalculate(true)
    try {
      const response = await calculatePrices({
        quoteId: record['@id'],
        maxPrice: true,
      })
      setFinalPrice(Number(response.data.prediction.max_price).toFixed(2))

      if (record.leadContact.vehicle.vehicleSalvage.chosenValue) {
        await calculateCommission({
          vehicle: record.leadContact.vehicle['@id'],
          recalculate: true,
        })
      }

      if (record.leadContact.vehicle.vehicleSalvage.highAdjustmentPercentage) {
        await calculateAdjustmentPrice(
          record['@id'],
          'cost',
          record.leadContact.vehicle.vehicleSalvage.highAdjustmentCost
        )
      }

      window.dispatchEvent(new Event('fetchPricingData'))
    } catch (error) {
      notify('Error fetching prices', { type: 'error' })
    } finally {
      setIsLoadingCalculate(false)
    }
  }

  const processOffer = async (action) => {
    try {
      await autoSaveField({
        fields: [
          {
            classToInitialize: null,
            method: 'setChosenValue',
            quoteRequestId: record['@id'],
            value: chosenValue,
          },
        ],
        iri: record.leadContact.vehicle.vehicleSalvage['@id'],
      })

      const message =
        action === 'accept'
          ? `Customer accepted quote of ${chosenValue}`
          : `Customer declined quote of ${chosenValue}`

      sendLog(record.originId, message)

      await autoSaveField({
        fields: [
          {
            classToInitialize: null,
            method: 'setOfferJourneyFollowed',
            quoteRequestId: record['@id'],
            value: true,
          },
        ],
        iri: record.leadContact.vehicle.vehicleSalvage['@id'],
      })

      window.dispatchEvent(new Event('refreshAdjustments'))
      refresh()
      resetModalState()
      closeModal()
    } catch (error) {
      notify('Error saving data', { type: 'error' })
    }
  }

  // Fetch initial prices whenever modal opens
  useEffect(() => {
    if (activeModal !== 'bestPrice' || !record.originId) return

    const fetchPrices = async () => {
      setIsLoadingCalculate(true)
      try {
        const { data: updatedRecord } = await refetch()
        setTargetPrice(
          Number(updatedRecord.latestPricingPrediction.targetPrice).toFixed(2)
        )
        setGuidePrice(
          Number(updatedRecord.latestPricingPrediction.guidePrice).toFixed(2)
        )
      } catch (error) {
        notify('Error fetching initial prices', { type: 'error' })
      } finally {
        setIsLoadingCalculate(false)
      }
    }

    fetchPrices()
  }, [activeModal])

  useEffect(() => {
    if (activeModal !== 'bestPrice' || !sortedAdjustments?.length) return

    const adjustments = sortedAdjustments[0].adjustments

    setKeyAdjustments({
      leather: adjustments['Leather'].adjustment,
      satNav: adjustments['SAT Nav'].adjustment,
      keys: adjustments['Keys'].adjustment,
      history: adjustments['History'].adjustment,
    })
  }, [activeModal, sortedAdjustments])

  return (
    <React.Fragment>
      <Dialog
        disablebackdropclick="true"
        disableEnforceFocus
        hideBackdrop
        disableScrollLock
        fullWidth
        scroll={'paper'}
        open={activeModal === 'bestPrice'}
        aria-label="Best price"
        className="vehicle-adjustments"
        sx={{
          left: '50%',
          top: '50%',
          right: 'initial',
          bottom: 'initial',
          transform: 'translate(-50%, -50%)',
        }}
        PaperProps={{
          sx: {
            padding: 2,
          },
        }}
      >
        <DialogContent sx={{ overflow: 'initial', padding: 0 }}>
          <Form onSubmit={() => processOffer('accept')}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="h6" fontWeight={'bold'}>
                    Best Offer
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="number"
                  label="Guide price"
                  value={guidePrice}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="number"
                  label="Target price"
                  value={targetPrice}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  className="vehicle-adjustments__general"
                  sx={{
                    '& > div button': {
                      minHeight: 0,
                      padding: '4px 10px',
                    },
                  }}
                >
                  {['Leather', 'SAT Nav', 'Keys', 'History'].map((name) => (
                    <Grid item xs={name === 'History' ? 6 : 2} key={name}>
                      <Adjustment
                        adjustment={
                          sortedAdjustments[0].adjustments[name].adjustment
                        }
                        adjustmentSettings={
                          sortedAdjustments[0].adjustments[name]
                            .adjustmentSettings
                        }
                        onConfirmedChange={(confirmed) =>
                          setKeyAdjustments((prev) => ({
                            ...prev,
                            [name.toLowerCase()]: {
                              ...prev[name.toLowerCase()],
                              confirmed,
                            },
                          }))
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12}>
                <Typography component={'span'}>Price justification</Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 0,
                    mt: 1,
                    pl: 0,
                    columnCount: 2,
                    listStyle: 'none',
                  }}
                >
                  <li style={{ display: 'flex', gap: 2 }}>
                    <Typography>Desirable model?</Typography>
                  </li>
                  <li style={{ display: 'flex', gap: 2 }}>
                    <Typography>Desirable colour?</Typography>
                  </li>
                  <li style={{ display: 'flex', gap: 2 }}>
                    <Typography>Low milage?</Typography>
                  </li>
                  <li style={{ display: 'flex', gap: 2 }}>
                    <Typography>Automatic?</Typography>
                  </li>
                  <li style={{ display: 'flex', gap: 2 }}>
                    <Typography>Location?</Typography>
                  </li>
                  <li style={{ display: 'flex', gap: 2 }}>
                    <Typography>Pricing Notes!!</Typography>
                  </li>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  color={'primary'}
                  onClick={handleCalculate}
                  disabled={!allowCalculateMaxPriceButton}
                >
                  Calculate max price
                </Button>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  autoComplete="off"
                  label="Max price"
                  min={0}
                  value={finalPrice}
                  disabled={true}
                />
              </Grid>
              {isLoadingCalculate && (
                <Box
                  sx={{
                    position: 'fixed',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Grid>
            <DialogActions sx={{ padding: 0, alignItems: 'start' }}>
              <TextField
                autoComplete="off"
                type="number"
                label="Final price"
                value={chosenValue}
                disabled={!allowFinalPriceInput}
                onChange={(e) => setChosenValue(e.target.value)}
                sx={{ mr: '8px' }}
              />
              <Button
                type="button"
                color="error"
                variant="contained"
                label="Decline offer"
                onClick={() => processOffer('decline')}
                disabled={!chosenValue || chosenValue <= 0}
              >
                Decline
              </Button>
              <Button
                type="submit"
                variant="contained"
                label="Accept offer"
                disabled={!chosenValue || chosenValue <= 0}
              >
                Accept
              </Button>
            </DialogActions>
            {showSalvageMessage && (
              <Typography color="warning.main" fontWeight="bold" mb={2}>
                There could be a higher scrap price for this vehicle
              </Typography>
            )}
            {showRulePriceMessage && (
              <Typography color="warning.main" fontWeight="bold" mb={2}>
                This is a rule price, please check pricing notes
              </Typography>
            )}
          </Form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  )
}

BestPriceModal.propTypes = {
  sortedAdjustments: PropTypes.array,
}

export default BestPriceModal
